import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "../api";
import type { CursorPage, Datified, Schemas } from "../../api/types";
import { POLL_BASE_MS } from "./intervals";
import { qs } from "./query";

export type Product = Datified<Schemas["Product"], "created_at" | "updated_at">;
export type ProductInput = Schemas["ProductInput"];

export interface ProductFilters {
  q?: string;
  cursor?: string;
  limit?: number;
}

export function useProductList(companyId: string) {
  return function useList(filters: ProductFilters = {}) {
    return useQuery<CursorPage<Product>>({
      queryKey: ["companies", companyId, "products", filters],
      queryFn: () => apiGet<CursorPage<Product>>(`/companies/${companyId}/products${qs({ ...filters })}`),
      refetchInterval: POLL_BASE_MS,
    });
  };
}

export async function searchProducts(companyId: string, q: string): Promise<Product[]> {
  const page = await apiGet<CursorPage<Product>>(`/companies/${companyId}/products${qs({ q, limit: 20 })}`);
  return page.items;
}

export function useSaveProduct(companyId: string) {
  const qc = useQueryClient();
  return useMutation<Product, unknown, { id?: string; input: ProductInput }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, input }) =>
      id
        ? apiPut<Product>(`/companies/${companyId}/products/${id}`, input)
        : apiPost<Product>(`/companies/${companyId}/products`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["companies", companyId, "products"] }),
  });
}

export function useDeleteProduct(companyId: string) {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await apiDelete(`/companies/${companyId}/products/${id}`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["companies", companyId, "products"] }),
  });
}

export function useProduct(companyId: string, id: string | undefined) {
  return useQuery<Product>({
    queryKey: ["companies", companyId, "products", "one", id],
    queryFn: () => apiGet<Product>(`/companies/${companyId}/products/${id}`),
    enabled: !!id,
  });
}
